import { ConfigureSourceLinkSection } from '@modules/integrationsV2/components/ConfigureSourceLinkSection';
import {
  IntegrationSourceCohortsUnderConstruction,
  IntegrationSourceDetailAlertMessage,
  IntegrationSourceFileNotUploaded,
  IntegrationSourceIngestionInProgressCard,
  IntegrationSourceToolbar,
} from '@modules/integrationsV2/components/IntegrationsContainer/IntegrationsSourceDetailView';
import { IntegrationSourceConnectedIntegrations } from '@modules/integrationsV2/components/IntegrationsContainer/IntegrationsSourceDetailView/IntegrationSourceConnectedIntegrations/IntegrationSourceConnectedIntegrations';
import { LinkIntegrationMetadataSection } from '@modules/integrationsV2/components/LinkIntegrationMetadataSection';
import { ManageMetadataSection } from '@modules/integrationsV2/components/ManageMetadataSection';
import { useGetIntegrationsPageContentHeight } from '@modules/integrationsV2/hooks';
import { IntegrationSource } from '@modules/integrationsV2/types';
import { Box, Stack, Typography } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationType,
  IntegrationViewFragment,
} from 'generated/graphql';

interface IntegrationSourceDetailViewProps {
  integrationSource: IntegrationSource;
  integrations: IntegrationViewFragment[];
}

export const IntegrationSourceDetailView = ({
  integrationSource,
  integrations,
}: IntegrationSourceDetailViewProps) => {
  const height = useGetIntegrationsPageContentHeight({
    defaultHeight: '90vh',
  });

  const {
    channel: { type: channelType },
    namespace,
    integrationType,
    integrationID,
    isIngestionInProgress,
  } = integrationSource;

  const renderContent = () => {
    if (!integrations.length) {
      return (
        <Typography
          variant="h6"
          textAlign="center"
          mt={10}
          color="text.secondary"
        >
          No integrations found
        </Typography>
      );
    }

    const isFileUploadIntegration =
      channelType === IntegrationChannelType.FileUpload;

    const currentFileUploadIntegration = isFileUploadIntegration
      ? integrations.find((integration) => integration.id === integrationID)
      : undefined;

    const isEmptyFileUploadIntegration =
      currentFileUploadIntegration?.data?.fileUpload?.isEmpty;

    if (isEmptyFileUploadIntegration) {
      return (
        <IntegrationSourceFileNotUploaded
          selectedIntegrationSource={integrationSource}
        />
      );
    }

    if (isIngestionInProgress && integrationSource.isSingleConnectionSource) {
      return (
        <IntegrationSourceIngestionInProgressCard
          integrationSource={integrationSource}
        />
      );
    }

    return (
      <Stack spacing={4} width="100%">
        <IntegrationSourceDetailAlertMessage
          integrationSource={integrationSource}
        />
        <IntegrationSourceConnectedIntegrations
          integrationSource={integrationSource}
          integrations={integrations}
        />
        <ManageMetadataSection integrationSource={integrationSource} />
        <ConfigureSourceLinkSection
          integrationSource={integrationSource}
          integrations={integrations}
        />
        <LinkIntegrationMetadataSection
          integrationSource={integrationSource}
          integrations={integrations}
        />
        {integrationType === IntegrationType.Cohorts && (
          <IntegrationSourceCohortsUnderConstruction />
        )}
      </Stack>
    );
  };

  return (
    <Stack
      key={`${channelType}_${integrationType}_${namespace}`}
      width="100%"
      minWidth={0}
      sx={{
        overflowY: 'auto',
        maxHeight: height,
        pr: 2,
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          py: 3,
          bgcolor: 'background.default',
          zIndex: 10,
        }}
      >
        <IntegrationSourceToolbar
          integrationSource={integrationSource}
          integrations={integrations}
        />
      </Box>
      {renderContent()}
    </Stack>
  );
};
